import { inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../../features/auth/services/auth.service';
import { AppToasterService } from '../services/toaster.service';
import { SubdomainService } from '../services/subdomain.service';
import { toValvurHttpError } from '../errors/parse-valvur-api-error';
import { VALVUR_ERROR_CODE } from '../errors/valvur-error-codes';
import { isPublicAuthRoute } from '../helpers/public-auth-route.helper';

export class ErrorInterceptor implements HttpInterceptor {
  #authService = inject(AuthService);
  #toastr = inject(AppToasterService);
  #subdomain = inject(SubdomainService);
  #router = inject(Router);

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        const valvurError = toValvurHttpError(error);
        const { statusCode, errorCode, message } = valvurError.apiError;

        if (statusCode === 401) {
          if (isPublicAuthRoute(this.#router.url)) {
            this.#authService.clearSession();
          } else {
            this.#toastr.error(message);
            this.#authService.logout();
          }
        } else if (this.#shouldShowGlobalToast(errorCode, statusCode)) {
          this.#toastr.error(message);
        } else if (!this.#shouldSuppressDevLog(error)) {
          console.error('HTTP Error:', {
            statusCode,
            errorCode,
            message,
            url: error.url,
          });
        }

        return throwError(() => valvurError);
      }),
    );
  }

  /** Avoid duplicate toasts for validation errors handled in forms. */
  #shouldShowGlobalToast(errorCode: string, statusCode: number): boolean {
    if (
      errorCode === 'COMMON_VALIDATION_FAILED' ||
      errorCode === 'AUTH_USERNAME_INVALID' ||
      errorCode === 'AUTH_USER_ALREADY_EXISTS_REGISTER' ||
      errorCode === 'AUTH_IDENTITY_PARENT_KYC_REQUIRED'
    ) {
      return false;
    }
    if (statusCode >= 500 && errorCode === VALVUR_ERROR_CODE.UNKNOWN) {
      return true;
    }
    return false;
  }

  /** Parent identity endpoints are family-only; 403 on admin/patrol/school is expected. */
  #shouldSuppressDevLog(error: HttpErrorResponse): boolean {
    if (error.status !== 403 || !error.url) {
      return false;
    }
    if (this.#subdomain.isFamilySubdomain()) {
      return false;
    }
    return /\/user\/identity(\/config)?(\?|$)/.test(error.url);
  }
}
